import React, {
 ChangeEvent,
 DragEvent,
 InputHTMLAttributes,
 useRef,
 useState,
} from 'react'
import {FieldValues, FieldErrors} from 'react-hook-form'
import {ErrorMessage} from '@hookform/error-message'
import {KTSVG} from '../KTSVG'
import {useTranslation} from 'react-i18next'
import {
 formatFileSize,
 getDisplayNameFromUrl,
 isImageFileType,
} from 'utils/helpers/fileDisplay'

interface Props
 extends InputHTMLAttributes<HTMLInputElement> {
 label?: string
 wrapperLabel?: string
 filePath?: string
 required?: boolean
 id?: string
 name: string
 srcType?: string
 popupHeader?: string
 showPopupWindow?: boolean
 disabled?: boolean
 isProfilePic?: string
 signedPhoto?: string
 fileName?: string
 fileSize?: number
 errorText?: string
 className?: string
 wrapperClassName?: string
 fileType?: string
 fileTypes?: string
 fileTypesClassName?: string
 labelClassName?: string
 value?: string
 showViewFileBtn?: boolean
 register?: FieldValues
 errors?: FieldErrors | undefined
 hideUploadIcon?: boolean
 buttonBox?: React.ReactNode
 uploadedActionsBox?: React.ReactNode
 innerBox?: React.ReactNode
 validFileText?: string
 uploadHintText?: string
 uploadType?: string
 onFileDrop?: (file: File) => void | Promise<void>
}

const FileField = ({
 label,
 signedPhoto,
 buttonBox,
 uploadedActionsBox,
 innerBox,
 wrapperClassName,
 fileTypesClassName,
 labelClassName,
 fileTypes,
 fileType,
 popupHeader,
 disabled,
 name,
 id,
 onChange,
 register,
 errors,
 required,
 fileName,
 fileSize,
 validFileText,
 uploadHintText,
 onFileDrop,
 className,
}: Props) => {
 const {t} = useTranslation()
 const fileInputRef = useRef<HTMLInputElement | null>(null)
 const [isDragging, setIsDragging] = useState(false)
 const inputId = id ?? name

 const {
  ref: registerRef,
  onChange: registerOnChange,
  ...registerRest
 } = register ?? {}

 const setInputRef = (el: HTMLInputElement | null) => {
  fileInputRef.current = el
  if (typeof registerRef === 'function') {
   registerRef(el)
  }
 }

 const handleInputChange = (
  e: ChangeEvent<HTMLInputElement>
 ) => {
  registerOnChange?.(e)
  onChange?.(e)
 }

 const handleProceedBtnClick = () => {
  if (!disabled) {
   fileInputRef.current?.click()
  }
 }

 const openFile = () => {
  if (!signedPhoto || disabled) return
  window.open(signedPhoto, '_blank', 'noopener,noreferrer')
 }

 const stopEventPropagation = (
  e: React.MouseEvent<HTMLElement>
 ) => {
  e.stopPropagation()
 }

 const handleCardClick = () => {
  openFile()
 }

 const handleCardKeyDown = (
  e: React.KeyboardEvent<HTMLDivElement>
 ) => {
  if (disabled) return
  if (e.key === 'Enter' || e.key === ' ') {
   e.preventDefault()
   openFile()
  }
 }

 const processFile = async (file: File) => {
  if (onFileDrop) {
   await onFileDrop(file)
   return
  }
  const input = fileInputRef.current
  if (!input || disabled) return

  input.value = ''
  const dataTransfer = new DataTransfer()
  dataTransfer.items.add(file)
  input.files = dataTransfer.files

  const event = {
   target: input,
   currentTarget: input,
   type: 'change',
   bubbles: true,
  } as ChangeEvent<HTMLInputElement>

  handleInputChange(event)
 }

 const handleDragEnter = (e: DragEvent<HTMLElement>) => {
  e.preventDefault()
  e.stopPropagation()
  if (!disabled) setIsDragging(true)
 }

 const handleDragOver = (e: DragEvent<HTMLElement>) => {
  e.preventDefault()
  e.stopPropagation()
  if (!disabled) setIsDragging(true)
 }

 const handleDragLeave = (e: DragEvent<HTMLElement>) => {
  e.preventDefault()
  e.stopPropagation()
  setIsDragging(false)
 }

 const handleDrop = (e: DragEvent<HTMLElement>) => {
  e.preventDefault()
  e.stopPropagation()
  setIsDragging(false)
  if (disabled) return
  const file = e.dataTransfer.files?.[0]
  if (file) {
   void processFile(file)
  }
 }

 const dropzoneHandlers = disabled
  ? {}
  : {
     onDragEnter: handleDragEnter,
     onDragOver: handleDragOver,
     onDragLeave: handleDragLeave,
     onDrop: handleDrop,
    }

 const handleEmptyKeyDown = (
  e: React.KeyboardEvent<HTMLDivElement>
 ) => {
  if (disabled) return
  if (e.key === 'Enter' || e.key === ' ') {
   e.preventDefault()
   handleProceedBtnClick()
  }
 }

 const displayFileName =
  fileName ||
  getDisplayNameFromUrl(signedPhoto) ||
  label ||
  popupHeader ||
  'Document'
 const fileSizeLabel = formatFileSize(fileSize)
 const showImageThumb = isImageFileType(
  fileType,
  signedPhoto
 )
 const emptyHintText =
  uploadHintText ||
  (label || popupHeader
   ? `Click to upload ${(
      label || popupHeader
     )?.toLowerCase()} or drag & drop`
   : 'Click to upload or drag & drop')
 const emptyHelperText =
  validFileText || t('common.supportedFileFormats')

 const actionBtnClass =
  'inline-flex items-center justify-center w-9 h-9 rounded-md border border-[#D7DFEA] bg-white text-[#5A6066] cursor-pointer shrink-0 disabled:opacity-50 disabled:cursor-not-allowed'

 const renderErrors = () =>
  errors ? (
   <ErrorMessage
    errors={errors}
    name={name}
    render={({message}) => (
     <p className='error-msg error-cls'>{message}</p>
    )}
   />
  ) : null

 const renderHiddenInput = () => (
  <input
   {...registerRest}
   ref={setInputRef}
   type='file'
   name={name}
   id={inputId}
   className='hidden'
   placeholder=' '
   disabled={disabled}
   onChange={handleInputChange}
  />
 )

 const emptyDropzoneClassName = `flex items-center gap-3 w-full max-w-full min-h-[72px] rounded-lg border border-dashed px-4 py-3 ${
  isDragging
   ? 'border-[#4E83E4] bg-[#EAF2FF]'
   : 'border-[#D7DFEA] bg-[#F5F8FA]'
 } ${
  disabled
   ? 'opacity-50 cursor-not-allowed pointer-events-none'
   : 'cursor-pointer'
 }`

 const uploadedCardClassName = `group flex items-center justify-between gap-4 w-full max-w-full min-h-[72px] rounded-lg border px-4 py-3 ${
  isDragging ? 'border-[#4E83E4] bg-[#EAF2FF]' : 'bg-white'
 } ${
  disabled
   ? 'border-[#D7DFEA] opacity-50 cursor-not-allowed'
   : 'cursor-pointer border-[#D7DFEA]'
 }`

 const renderUploadIconBox = () => (
  <div className='flex items-center justify-center w-10 h-10 rounded-lg bg-[#EAF2FF] shrink-0'>
   <KTSVG
    path='/media/icons/uploadFile.svg'
    svgClassName='w-5 h-5 shrink-0 text-[#4E83E4]'
   />
  </div>
 )

 const renderLabel = () =>
  label && (
   <label
    className={`input-label block ${labelClassName} relative pb-1 text-[#4E83E4] text-xs font-medium mb-2`}
   >
    {label}
    {required && (
     <span className='required-field pl-[2px]'>*</span>
    )}
   </label>
  )

 if (signedPhoto) {
  return (
   <>
    <div
     className={`block w-full max-w-full ${
      className ?? ''
     } ${wrapperClassName ?? ''}`}
    >
     {renderLabel()}
     <div
      className={uploadedCardClassName}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role='button'
      tabIndex={disabled ? -1 : 0}
      aria-label='Open uploaded document'
      {...dropzoneHandlers}
     >
      <div className='flex items-center gap-3 min-w-0 flex-1'>
       <div className='relative w-12 h-12 rounded-md overflow-hidden bg-white border border-[#E6EBF0] flex items-center justify-center shrink-0'>
        {showImageThumb ? (
         <img
          src={signedPhoto}
          alt={displayFileName}
          className='w-full h-full object-cover'
         />
        ) : fileType === 'video' ? (
         <i
          className='fa fa-file-video-o text-[#1C88ED] text-xl'
          aria-hidden='true'
         />
        ) : (
         <i
          className='fa fa-file-pdf-o text-[#1C88ED] text-xl'
          aria-hidden='true'
         />
        )}
        {!disabled && (
         <div className='pointer-events-none absolute inset-0 flex items-center justify-center bg-[rgba(23,27,31,0.45)] opacity-0 transition-opacity duration-200 group-hover:opacity-100'>
          <i
           className='fa fa-arrows-alt text-white text-base'
           aria-hidden='true'
          />
         </div>
        )}
       </div>
       <div className='flex flex-col min-w-0 flex-1'>
        <span className='text-[#33393D] text-sm font-medium truncate'>
         {displayFileName}
        </span>
        <span className='flex items-center gap-1.5 text-xs mt-0.5'>
         <i
          className='fa fa-check-circle text-[#30835A]'
          aria-hidden='true'
         />
         <span className='text-[#30835A] font-medium'>
          Uploaded
         </span>
         {fileSizeLabel && (
          <>
           <span className='text-[#80868C]'>•</span>
           <span className='text-[#80868C]'>
            {fileSizeLabel}
           </span>
          </>
         )}
        </span>
       </div>
      </div>

      <div
       className='flex items-center gap-2 shrink-0'
       onClick={stopEventPropagation}
      >
       <button
        type='button'
        className={actionBtnClass}
        onClick={handleProceedBtnClick}
        aria-label='Replace document'
        disabled={disabled}
       >
        <KTSVG
         path='/media/icons/uploadIcon.svg'
         svgClassName='w-4 h-4 shrink-0 text-[#5A6066]'
        />
       </button>
       {uploadedActionsBox}
      </div>
     </div>
     {renderErrors()}
     {renderHiddenInput()}
     {buttonBox}
    </div>
   </>
  )
 }

 const renderEmptyState = () => {
  if (innerBox) {
   return (
    <div
     className={emptyDropzoneClassName}
     onClick={disabled ? undefined : handleProceedBtnClick}
     onKeyDown={handleEmptyKeyDown}
     role='button'
     tabIndex={disabled ? -1 : 0}
     aria-label='Upload document'
     {...dropzoneHandlers}
    >
     {renderUploadIconBox()}
     <div className='w-full min-w-0'>{innerBox}</div>
    </div>
   )
  }

  return (
   <div
    className={emptyDropzoneClassName}
    onClick={disabled ? undefined : handleProceedBtnClick}
    onKeyDown={handleEmptyKeyDown}
    role='button'
    tabIndex={disabled ? -1 : 0}
    aria-label='Upload document'
    {...dropzoneHandlers}
   >
    {renderUploadIconBox()}
    <div className='flex flex-col min-w-0'>
     <span className='text-[#33393D] text-sm font-medium'>
      {emptyHintText}
     </span>
     <span className='text-[#80868C] text-xs mt-0.5'>
      {emptyHelperText}
     </span>
    </div>
   </div>
  )
 }

 return (
  <div
   className={`block w-full max-w-full ${className ?? ''} ${
    wrapperClassName ?? ''
   } ${disabled ? 'opacity-50' : ''}`}
  >
   {renderLabel()}
   {renderEmptyState()}
   {fileTypes && (
    <p
     className={`text-xs text-gray-500 dark:text-gray-400 mt-1 ${fileTypesClassName}`}
    >
     {fileTypes}
    </p>
   )}
   {renderErrors()}
   {renderHiddenInput()}
   {buttonBox}
  </div>
 )
}

export {FileField}
